/**
 * Motor "contar" — tocar objetos um a um; cada toque anima e narra o numero.
 *
 * D13 (escada concreto -> figura -> simbolo), aplicada pelas bandas:
 *   banda 1: so os objetos. mostra_numeral: false — nenhum numeral na tela.
 *   banda 2: objetos + o numeral aparecendo a cada toque.
 *   banda 3: objetos agrupados (agrupar_por), para contar de 5 em 5.
 *
 * D6: nao existe "errado". Tocar duas vezes no mesmo buraco nao penaliza nada;
 * a semente ja esta la e o jogo so espera os que faltam.
 * D4: cada toque tem retorno em menos de 200 ms — som, brilho e movimento.
 */

import * as THREE from 'three';
import { BaseEngine } from './BaseEngine.js';
import { posicoesDeBuracos, ESPACAMENTO_BURACO } from '../world/Ancoras.js';

// D3 — alvo minimo de 80x80 px. Com a camera focada na ancora, um buraco deste
// raio passa de 80 px de diametro na tela do Chromebook.
const RAIO_BURACO = 0.52;

export class ContarEngine extends BaseEngine {
  constructor(ctx) {
    super(ctx);
    this.buracos = [];
    this.plantados = 0;
    this.grupo = null;
    this.animando = [];
  }

  async iniciar(tarefa, banda) {
    await super.iniciar(tarefa, banda);

    const payload = tarefa.bandas[String(banda)];
    if (!payload) {
      throw new Error(
        `FAZENDA: a tarefa "${tarefa.id}" nao tem payload para a banda ${banda}. ` +
          'Toda tarefa precisa das bandas 1, 2 e 3 (secao 6).'
      );
    }

    const [min, max] = payload.quantidade;
    this.quantidade = min + Math.floor(Math.random() * (max - min + 1));
    this.mostraNumeral = payload.mostra_numeral === true;
    this.agruparPor = payload.agrupar_por ?? null;
    this.cultura = sortear(tarefa.conteudo?.sementes ?? ['milho']);

    this.montarBuracos();

    // D8 nao se aplica aqui: nao e uma escolha entre opcoes, e contagem.
    console.info(
      `FAZENDA: tarefa "${tarefa.id}" iniciada — banda ${banda}, ` +
        `${this.quantidade} buracos, numeral ${this.mostraNumeral ? 'visivel' : 'escondido'}.`
    );
  }

  montarBuracos() {
    const { fazenda, sceneManager, input } = this.ctx;

    this.grupo = new THREE.Group();
    this.grupo.name = 'tarefa-contar';

    const base = fazenda.posicaoDaAncora(this.tarefa.ancora);
    const porLinha = this.agruparPor ?? 5;

    // Mesmo calculo que a Fazenda usa para as plantas nascerem nos buracos.
    const posicoes = posicoesDeBuracos(base, this.quantidade, porLinha);

    for (let i = 0; i < this.quantidade; i++) {
      const buraco = new THREE.Mesh(
        new THREE.CylinderGeometry(RAIO_BURACO, RAIO_BURACO * 0.8, 0.2, 10),
        new THREE.MeshStandardMaterial({ color: 0x4a2f18, roughness: 1, flatShading: true })
      );

      buraco.position.set(posicoes[i].x, base.y + 0.4, posicoes[i].z);

      buraco.userData.indice = i;
      buraco.userData.plantado = false;

      this.grupo.add(buraco);
      this.buracos.push(buraco);

      input.registrar(buraco, () => this.aoTocarBuraco(buraco));
    }

    sceneManager.adicionar(this.grupo);

    // D3 — confere se o alvo ficou grande o bastante na tela.
    input.conferirTamanhoDeAlvo(this.buracos[0], `buraco da tarefa ${this.tarefa.id}`);

    // Banda 3: separa visualmente os grupos de 5, para contar de 5 em 5.
    if (this.agruparPor) this.marcarGrupos(porLinha);
  }

  marcarGrupos(porLinha) {
    const base = this.ctx.fazenda.posicaoDaAncora(this.tarefa.ancora);
    const linhas = Math.ceil(this.quantidade / porLinha);
    for (let linha = 0; linha < linhas; linha++) {
      const primeiro = this.buracos[linha * porLinha];
      if (!primeiro) continue;

      const faixa = new THREE.Mesh(
        new THREE.BoxGeometry(porLinha * ESPACAMENTO_BURACO + 0.4, 0.02, ESPACAMENTO_BURACO),
        new THREE.MeshStandardMaterial({
          color: linha % 2 === 0 ? 0xffe082 : 0xc5e1a5,
          roughness: 1,
          transparent: true,
          opacity: 0.5,
        })
      );
      faixa.position.set(base.x, base.y + 0.32, primeiro.position.z);
      this.grupo.add(faixa);
    }
  }

  aoTocarBuraco(buraco) {
    const { audio, instrucao } = this.ctx;

    // D6 — tocar de novo no mesmo buraco nao e erro, nao penaliza, nao faz som
    // de errado. Simplesmente ja esta plantado.
    if (buraco.userData.plantado) {
      audio.tocarTentarDeNovo();
      return;
    }

    buraco.userData.plantado = true;
    this.plantados += 1;

    // D4 — som imediato...
    audio.tocarToque(this.plantados - 1);

    // ...e a semente aparecendo com uma animacao curta.
    const semente = this.criarSemente(buraco);
    this.animando.push({ objeto: semente, t: 0, alturaFinal: buraco.position.y + 0.45 });

    // D13 — o numeral so aparece se a banda permitir.
    if (this.mostraNumeral) {
      instrucao.mostrarNumeral(this.plantados);
    }

    if (this.plantados >= this.quantidade) {
      this.aoTerminar();
    }
  }

  criarSemente(buraco) {
    const cor = { milho: 0xf9d71c, feijao: 0x66bb6a, abobora: 0xff8f00 }[this.cultura] ?? 0x66bb6a;

    const semente = new THREE.Mesh(
      new THREE.IcosahedronGeometry(0.22, 0),
      new THREE.MeshStandardMaterial({ color: cor, roughness: 0.9, flatShading: true })
    );
    semente.position.copy(buraco.position);
    semente.position.y += 0.9; // cai de cima
    this.grupo.add(semente);
    return semente;
  }

  aoTerminar() {
    const { audio, input } = this.ctx;

    for (const buraco of this.buracos) input.desregistrar(buraco);
    audio.tocarSucesso();

    this.pronto = true;
  }

  atualizar(dt) {
    // Sementes caindo nos buracos.
    for (let i = this.animando.length - 1; i >= 0; i--) {
      const anim = this.animando[i];
      anim.t = Math.min(1, anim.t + dt * 4);

      const suave = 1 - (1 - anim.t) ** 3;
      anim.objeto.position.y =
        anim.objeto.position.y + (anim.alturaFinal - anim.objeto.position.y) * suave;
      anim.objeto.rotation.y += dt * 6;

      if (anim.t >= 1) this.animando.splice(i, 1);
    }
  }

  /** true quando a crianca plantou tudo. */
  concluiu() {
    return this.plantados >= this.quantidade;
  }

  async concluir() {
    const evento = await super.concluir({ concluiu: this.concluiu() });
    return {
      ...evento,
      // O que o efeito canteiro.plantar precisa saber:
      cultura: this.cultura,
      quantidade: this.quantidade,
    };
  }

  descartar() {
    const { input, sceneManager, instrucao } = this.ctx;

    for (const buraco of this.buracos) input.desregistrar(buraco);

    if (this.grupo) {
      sceneManager.remover(this.grupo);
      // Sem vazamento de memoria: geometria e material sao liberados na mao.
      this.grupo.traverse((objeto) => {
        objeto.geometry?.dispose();
        objeto.material?.dispose();
      });
    }

    instrucao.limparNumeral();

    this.buracos = [];
    this.animando = [];
    this.grupo = null;
    this.plantados = 0;

    super.descartar();
  }
}

function sortear(lista) {
  return lista[Math.floor(Math.random() * lista.length)];
}
